import type { ReactNode } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@athena/ui";

/**
 * A confirmation for an action that is not a deletion.
 *
 * `DeleteDialog`'s copy is deletion's — "are you sure you want to delete", a Delete button, "this
 * cannot be undone". Reusing it for a reset would put the wrong verb on the button, and the rule in
 * `.claude/rules/ui.md` is explicit that the confirm button repeats the action rather than saying
 * OK: somebody reading only the button should know what they are agreeing to.
 *
 * The caller supplies the description because only the caller knows the blast radius. "Re-capture
 * the poster for 620 videos" is a different decision from "re-capture this one", and a dialog that
 * says neither is a dialog nobody reads twice.
 */
interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    /**
     * What will happen, in the caller's own words, including how many rows it touches. Formatted text
     * is allowed, so the number can stand out.
     */
    description: ReactNode;
    /** The verb, repeated. "Reset poster", not "OK". */
    confirmLabel: string;
    /** Shown on the button while the action runs. */
    loadingLabel?: string;
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
    /** When set, the action cannot proceed — explains why, with a single dismiss. */
    blockedReason?: string | null;
    /**
     * The way out, when "Cancel" would be ambiguous — beside "Cancel upload", it reads as the same
     * action. "Keep uploading" says what declining actually does.
     */
    dismissLabel?: string;
}

export const ConfirmDialog = ({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel,
    loadingLabel,
    onConfirm,
    loading = false,
    blockedReason,
    dismissLabel = "Cancel",
}: ConfirmDialogProps) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{blockedReason ?? description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
                {blockedReason ? (
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Got it
                    </Button>
                ) : (
                    <>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            {dismissLabel}
                        </Button>
                        <Button disabled={loading} onClick={onConfirm}>
                            {loading ? (loadingLabel ?? `${confirmLabel}…`) : confirmLabel}
                        </Button>
                    </>
                )}
            </DialogFooter>
        </DialogContent>
    </Dialog>
);
